/**
 * createScopedPermissionGuard — route guard for SCOPED permissions, the
 * `OrganizationContextStore` counterpart to `createPermissionGuard` (which gates
 * on flat `AuthStore` permissions).
 *
 * Allows the route iff the user holds `entity.action` in the active organization
 * (any scope, or at least `minScope`). Waits for the context store to finish its
 * initial resolution so a hard refresh doesn't flash `/unauthorized` while scopes
 * are still in-flight. ADMIN/SUPER_ADMIN pass automatically — the server resolves
 * every catalog key to scope `all` for them.
 *
 * Pair it AFTER `authGuard` (and the context store hydrates off the auth session):
 * ```ts
 * export const routeMeta: RouteMeta = {
 *   canActivate: [authGuard, createScopedPermissionGuard('organigram', 'read')],
 * };
 * ```
 */
import { inject } from '@angular/core';
import type { CanActivateFn } from '@angular/router';
import { Router } from '@angular/router';
import { AuthStore } from '@mb/auth/client';
import { clientLogger } from '@mb/common/client';
import type { PermissionAction, PermissionScope } from '@mb/auth/shared';
import { OrganizationContextStore } from '../state/organization-context.state';

export interface ScopedPermissionGuardOptions {
  /** Require at least this scope breadth; omit to allow any granted scope. */
  minScope?: PermissionScope;
  /**
   * Flat permissions that open the route as well — a second way in, as the
   * time accounts have: the payroll office through `EMPLOYMENT_ADMIN`, an
   * employee through `timeAccount.read` (UI-013).
   */
  orPermissions?: readonly string[];
  /** Max ms to wait for the context store to initialize on first refresh. */
  maxWaitMs?: number;
  /** Polling interval while waiting for `isInitialized`. */
  pollIntervalMs?: number;
}

export function createScopedPermissionGuard(
  entity: string,
  action: PermissionAction,
  options: ScopedPermissionGuardOptions = {},
): CanActivateFn {
  const minScope = options.minScope;
  const maxWaitMs = options.maxWaitMs ?? 10_000;
  const pollIntervalMs = options.pollIntervalMs ?? 50;

  return (_route, state) => {
    // SSR: defer to the client (no session/context here) — matches authGuard.
    if (typeof window === 'undefined') {
      return true;
    }

    const orgContext = inject(OrganizationContextStore);
    const authStore = inject(AuthStore);
    const router = inject(Router);

    const evaluate = (): boolean => {
      if (orgContext.can(entity, action, minScope)) {
        return true;
      }
      const held = authStore.userPermissions() ?? [];
      if ((options.orPermissions ?? []).some((p) => held.includes(p))) {
        return true;
      }
      clientLogger.warn('[SCOPED PERMISSION GUARD] Access denied', {
        url: state.url,
        entity,
        action,
        minScope,
      });
      void router.navigate(['/unauthorized'], {
        queryParams: {
          returnUrl: state.url,
          requiredScoped: `${entity}.${action}${minScope ? `@${minScope}` : ''}`,
        },
      });
      return false;
    };

    // Ready once the context store has resolved, or there's no session to wait
    // for (authGuard will handle the unauthenticated redirect).
    const isReady = () =>
      orgContext.isInitialized() ||
      (authStore.isInitialized() && !authStore.isAuthenticated());

    if (isReady()) {
      return evaluate();
    }

    return new Promise<boolean>((resolve) => {
      const startedAt = Date.now();
      const interval = setInterval(() => {
        if (isReady()) {
          clearInterval(interval);
          resolve(evaluate());
          return;
        }
        if (Date.now() - startedAt > maxWaitMs) {
          clearInterval(interval);
          clientLogger.error('[SCOPED PERMISSION GUARD] Context init timeout', {
            url: state.url,
            maxWaitMs,
          });
          resolve(evaluate());
        }
      }, pollIntervalMs);
    });
  };
}
